import { useDarkMode } from "../ThemeContext";
import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import VideoModal from "../components/VideoModal";
import "../styles/Pages.css";
import "../styles/Vachanamrut.css";
import paramrutData from "../data/paramrut4.json";

const PARAMRUT_INDEX_URL = "/data/paramrut-index.json";
const LAST_READ_KEY = "paramrut-last-read";


const ROMAN_CONSONANTS = {
  ksh: "ક્ષ",
  chh: "છ",
  kh: "ખ",
  gh: "ઘ",
  ch: "ચ",
  jh: "ઝ",
  th: "થ",
  dh: "ધ",
  ph: "ફ",
  bh: "ભ",
  sh: "શ",
  gn: "જ્ઞ",
  tr: "ત્ર",
  ny: "ન્ય",
  ng: "ઙ",
  k: "ક",
  g: "ગ",
  c: "ક",
  j: "જ",
  t: "ત",
  d: "દ",
  n: "ન",
  p: "પ",
  b: "બ",
  m: "મ",
  y: "ય",
  r: "ર",
  l: "લ",
  v: "વ",
  w: "વ",
  s: "સ",
  h: "હ",
  f: "ફ",
  q: "ક",
  x: "ક્સ",
  z: "ઝ",
};
const ROMAN_VOWELS = {
  aa: { independent: "આ", matra: "ા" },
  ai: { independent: "ઐ", matra: "ૈ" },
  au: { independent: "ઔ", matra: "ૌ" },
  ii: { independent: "ઈ", matra: "ી" },
  ee: { independent: "ઈ", matra: "ી" },
  oo: { independent: "ઊ", matra: "ૂ" },
  uu: { independent: "ઊ", matra: "ૂ" },
  a: { independent: "અ", matra: "" },
  i: { independent: "ઇ", matra: "િ" },
  u: { independent: "ઉ", matra: "ુ" },
  e: { independent: "એ", matra: "ે" },
  o: { independent: "ઓ", matra: "ો" },
};
const GUJARATI_ROMAN = {
  "અ": "a",
  "આ": "aa",
  "ઇ": "i",
  "ઈ": "i",
  "ઉ": "u",
  "ઊ": "u",
  "ઋ": "ri",
  "એ": "e",
  "ઐ": "ai",
  "ઓ": "o",
  "ઔ": "au",
  "ક": "k",
  "ખ": "kh",
  "ગ": "g",
  "ઘ": "gh",
  "ઙ": "ng",
  "ચ": "ch",
  "છ": "chh",
  "જ": "j",
  "ઝ": "jh",
  "ઞ": "ny",
  "ટ": "t",
  "ઠ": "th",
  "ડ": "d",
  "ઢ": "dh",
  "ણ": "n",
  "ત": "t",
  "થ": "th",
  "દ": "d",
  "ધ": "dh",
  "ન": "n",
  "પ": "p",
  "ફ": "ph",
  "બ": "b",
  "ભ": "bh",
  "મ": "m",
  "ય": "y",
  "ર": "r",
  "લ": "l",
  "ળ": "l",
  "વ": "v",
  "શ": "sh",
  "ષ": "sh",
  "સ": "s",
  "હ": "h",
};
const GUJARATI_MATRAS = {
  "ા": "aa",
  "િ": "i",
  "ી": "i",
  "ુ": "u",
  "ૂ": "u",
  "ૃ": "ri",
  "ે": "e",
  "ૈ": "ai",
  "ો": "o",
  "ૌ": "au",
};

function writeCookie(name, value, days = 365) {
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/;SameSite=Lax`;
}

function readCookie(name) {
  const pair = document.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  return pair ? decodeURIComponent(pair.slice(name.length + 1)) : "";
}

function saveLastRead(item) {
  const value = JSON.stringify({
    prvid: item.prvid,
    prvfile: item.prvfile,
    prvtitle: item.prvtitle,
  });
  try {
    localStorage.setItem(LAST_READ_KEY, value);
  } catch {
    // Cookies remain available when local storage is disabled.
  }
  writeCookie(LAST_READ_KEY, value);
}

function loadLastRead() {
  let value = "";
  try {
    value = localStorage.getItem(LAST_READ_KEY) || "";
  } catch {
    // Fall back to the cookie below.
  }
  if (!value) value = readCookie(LAST_READ_KEY);
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed?.prvfile ? parsed : null;
  } catch {
    return null;
  }
}

function normalizeRoman(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^a-z]/g, "")
    .replace(/aa/g, "a")
    .replace(/ee|ii/g, "i")
    .replace(/oo|uu/g, "u")
    .replace(/sh/g, "s")
    .replace(/ph/g, "f")
    .replace(/bh/g, "b")
    .replace(/dh/g, "d")
    .replace(/th/g, "t")
    .replace(/chh/g, "ch")
    .replace(/v/g, "w");
}

function normalizeGujarati(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[િી]/g, "િ")
    .replace(/[ુૂ]/g, "ુ")
    .replace(/[ેૈ]/g, "ે")
    .replace(/[ોૌ]/g, "ો")
    .replace(/[ંઃઁ]/g, "ં")
    .replace(/[શષસ]/g, "સ")
    .replace(/ળ/g, "લ");
}

function romanToGujarati(text) {
  const input = (text || "").toLowerCase().replace(/[^a-z\s]/g, "");
  const vowels = Object.keys(ROMAN_VOWELS).sort((a, b) => b.length - a.length);
  const consonants = Object.keys(ROMAN_CONSONANTS).sort(
    (a, b) => b.length - a.length,
  );
  let output = "";
  let index = 0;

  while (index < input.length) {
    if (/\s/.test(input[index])) {
      output += " ";
      index += 1;
      continue;
    }
    const remaining = input.slice(index);
    const vowel = vowels.find((token) => remaining.startsWith(token));
    if (vowel) {
      output += ROMAN_VOWELS[vowel].independent;
      index += vowel.length;
      continue;
    }
    const consonant = consonants.find((token) => remaining.startsWith(token));
    if (!consonant) {
      index += 1;
      continue;
    }
    output += ROMAN_CONSONANTS[consonant];
    index += consonant.length;
    const afterConsonant = input.slice(index);
    const nextVowel = vowels.find((token) => afterConsonant.startsWith(token));
    if (nextVowel) {
      output += ROMAN_VOWELS[nextVowel].matra;
      index += nextVowel.length;
    }
  }
  return output;
}

function gujaratiToRoman(text) {
  const input = text || "";
  let output = "";
  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];
    const next = input[index + 1] || "";
    if (/[\u0A95-\u0AB9]/.test(character)) {
      const consonant = GUJARATI_ROMAN[character];
      if (!consonant) continue;
      if (GUJARATI_MATRAS[next]) {
        output += consonant + GUJARATI_MATRAS[next];
        index += 1;
      } else if (next === "્") {
        output += consonant;
        index += 1;
      } else {
        output += `${consonant}a`;
      }
      continue;
    }
    if (GUJARATI_ROMAN[character]) {
      output += GUJARATI_ROMAN[character];
      continue;
    }
    if (GUJARATI_MATRAS[character]) {
      output += GUJARATI_MATRAS[character];
      continue;
    }
    if (character === "ં" || character === "ઁ") output += "m";
    else if (character === "ઃ") output += "h";
    else output += " ";
  }
  return normalizeRoman(output.replace(/a(\s|$)/g, "$1"));
}

function searchTerms(query) {
  const terms = new Set([query.trim().toLowerCase()]);
  const isGujarati = /[\u0A80-\u0AFF]/.test(query);
  if (isGujarati) {
    terms.add(normalizeGujarati(query));
  } else if (/^[a-z\s]+$/i.test(query.trim())) {
    const transliterated = romanToGujarati(query);
    if (transliterated) terms.add(transliterated.toLowerCase());
    const normalized = normalizeGujarati(transliterated);
    if (normalized) terms.add(normalized);
  }
  return {
    terms: Array.from(terms).filter(Boolean),
    isRoman: !isGujarati && /^[a-z\s]+$/i.test(query.trim()),
    romanQuery: normalizeRoman(query),
  };
}

function highlightWords(html, word) {
  if (!word || word.trim().length < 2) return html;
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(${escaped})`, "gi");
  return html.replace(
    re,
    '<mark style="background:#ffe066;color:#222;border-radius:2px">$1</mark>',
  );
}

function getVideoId(url) {
  if (!url) return null;
  const m = url.match(/[?&]v=([^&]+)/);
  return m ? m[1] : null;
}

const ITEMS = [...paramrutData].sort(
  (a, b) => new Date(b.prvdate) - new Date(a.prvdate),
);

export default function Paramrut() {
  const [search, setSearch] = useState("");
  const [selectedYear, setSelectedYear] = useState("All");
  const [selected, setSelected] = useState(null);
  const [content, setContent] = useState("");
  const [contentLoading, setContentLoading] = useState(false);
  const [contentIndexLoading, setContentIndexLoading] = useState(true);
  const [contentIndexError, setContentIndexError] = useState("");
  const [fontSize, setFontSize] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalVideoId, setModalVideoId] = useState(null);
  const [modalVideoTitle, setModalVideoTitle] = useState(null);
  const [contentSearch, setContentSearch] = useState("");
  const [contentIndex, setContentIndex] = useState([]);
  const [lastRead, setLastRead] = useState(() => loadLastRead());
  const [darkMode, setDarkMode] = useDarkMode();
  const [contentSearchResults, setContentSearchResults] = useState(null);
  const activeRef = useRef(null);

  const years = [
    "All",
    ...Array.from(new Set(ITEMS.map((i) => i.prvyear))).sort((a, b) => b - a),
  ];

  useEffect(() => {
    let active = true;
    fetch(`${process.env.PUBLIC_URL}${PARAMRUT_INDEX_URL}`)
      .then((response) => {
        if (!response.ok) throw new Error("Could not load the Paramrut search index.");
        return response.json();
      })
      .then((data) => {
        if (active) {
          setContentIndex(data);
          setContentIndexLoading(false);
        }
      })
      .catch((error) => {
        if (active) {
          setContentIndexError(error.message);
          setContentIndexLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  const handleContentSearch = () => {
    const word = contentSearch.trim();
    if (word.length < 2 || contentIndexLoading || contentIndexError) return;
    const { terms, isRoman, romanQuery } = searchTerms(word);
    const matchFiles = new Set(
      contentIndex
        .filter((entry) => {
          const lowerText = entry.text.toLowerCase();
          const normalizedText = normalizeGujarati(entry.text);
          const matchesText = terms.some(
            (term) =>
              lowerText.includes(term) || normalizedText.includes(term),
          );
          return (
            matchesText ||
            (isRoman &&
              romanQuery &&
              gujaratiToRoman(entry.text).includes(romanQuery))
          );
        })
        .map((entry) => entry.file),
    );
    setContentSearchResults(matchFiles);
  };

  const clearContentSearch = () => {
    setContentSearch("");
    setContentSearchResults(null);
  };

  const filtered = useMemo(
    () =>
      ITEMS.filter((item) => {
        if (selectedYear !== "All" && item.prvyear !== selectedYear)
          return false;
        if (
          contentSearchResults !== null &&
          !contentSearchResults.has(item.prvfile)
        )
          return false;
        if (search.trim()) {
          const q = search.toLowerCase();
          return (
            item.prvtitle?.toLowerCase().includes(q) ||
            item.prvinfo?.toLowerCase().includes(q) ||
            item.prvplace?.toLowerCase().includes(q) ||
            item.prvdate?.includes(q)
          );
        }
        return true;
      }),
    [contentSearchResults, search, selectedYear],
  );

  const selectedIndex = useMemo(
    () => filtered.findIndex((item) => item.prvid === selected?.prvid),
    [filtered, selected],
  );

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  const openItem = useCallback(
    async (item) => {
      setSelected(item);
      setLastRead({ prvid: item.prvid, prvfile: item.prvfile, prvtitle: item.prvtitle });
      saveLastRead(item);
      setContent("");
      setContentLoading(true);
      setFontSize(1);
      try {
        const res = await fetch(
          `${process.env.PUBLIC_URL}/data/paramrut/${item.prvfile}`,
        );
        if (!res.ok) throw new Error("File not found");
        let html = await res.text();
        html = html.replace(
          /<ul[^>]*class="reader_tools[^"]*"[^>]*>[\s\S]*?<\/ul>/i,
          "",
        );
        const terms = searchTerms(contentSearch.trim()).terms;
        const highlightTerm =
          terms.find((term) => term && html.toLowerCase().includes(term)) ||
          contentSearch.trim();
        html = highlightWords(html, highlightTerm);
        setContent(html);
      } catch {
        setContent("<p>Content could not be loaded.</p>");
      } finally {
        setContentLoading(false);
      }
    },
    [contentSearch],
  );

  const goToRelativeItem = useCallback(
    (delta) => {
      if (selectedIndex < 0) return;
      const target = filtered[selectedIndex + delta];
      if (target) openItem(target);
    },
    [filtered, openItem, selectedIndex],
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const tag = (target?.tagName || "").toLowerCase();
      if (
        !selected ||
        isModalOpen ||
        ["input", "textarea", "select", "button"].includes(tag) ||
        target?.isContentEditable
      )
        return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goToRelativeItem(-1);
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        goToRelativeItem(1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [goToRelativeItem, isModalOpen, selected]);

  const openLastRead = useCallback(() => {
    if (!lastRead) return;
    const item = ITEMS.find(
      (candidate) =>
        (lastRead.prvid && candidate.prvid === lastRead.prvid) ||
        candidate.prvfile === lastRead.prvfile,
    );
    if (item) openItem(item);
  }, [lastRead, openItem]);

  const openVideo = (item) => {
    const vid = getVideoId(item.prvvideourl);
    if (!vid) return;
    setModalVideoId(vid);
    setModalVideoTitle(item.prvtitle);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalVideoId(null);
    setModalVideoTitle(null);
  };

  return (
    <main
      className={`page-content pravachan-page paramrut-page scripture-reader${darkMode ? " sv-dark" : ""}`}
    >
      <section className="page-header">
        <h1 className="section-title">
          Paramrut
          <span className="paramrut-count">
            ({filtered.length})
          </span>
        </h1>
        <p className="header-subtitle">
          Guruhari P.P. Hariprasad Swamishri Pravachan
        </p>
        {lastRead && (
          <button
            type="button"
            className="last-read-vat"
            onClick={openLastRead}
            title="Open last read Paramrut"
          >
            Last Read: {lastRead.prvtitle}
          </button>
        )}
      </section>

      <section className="page-body">
        {/* Content (HTML text) search */}
        <div className="word-search-bar" style={{ marginBottom: "10px" }}>
          <input
            className="word-search-input"
            type="text"
            placeholder="Search pravachan text (Gujarati or English)..."
            value={contentSearch}
            onChange={(e) => setContentSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleContentSearch()}
          />
          <button
            type="button"
            className="word-search-btn"
            onClick={handleContentSearch}
            disabled={
              contentSearch.trim().length < 2 ||
              contentIndexLoading ||
              Boolean(contentIndexError)
            }
          >
            {contentIndexLoading ? "Loading index..." : "Search Text"}
          </button>
          {contentSearchResults !== null && (
            <button
              type="button"
              className="word-search-clear"
              onClick={clearContentSearch}
            >
              Clear ({contentSearchResults.size})
            </button>
          )}
          <button
            type="button"
            className="word-search-clear"
            onClick={() => setDarkMode((value) => !value)}
            title="Toggle light/dark mode"
          >
            {darkMode ? "Light" : "Dark"}
          </button>
        </div>
        {contentIndexError && (
          <p className="paramrut-search-error" role="alert">
            Text search unavailable: {contentIndexError}
          </p>
        )}
        {contentSearchResults !== null && (
          <p className="paramrut-search-count" aria-live="polite">
            {contentSearchResults.size} result
            {contentSearchResults.size === 1 ? "" : "s"} for "
            {contentSearch.trim()}"
          </p>
        )}

        {/* Title / place / info filter */}
        <div className="word-search-bar" style={{ marginBottom: "16px" }}>
          <input
            className="word-search-input"
            type="text"
            placeholder="Search title, place, info..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="word-search-clear" onClick={() => setSearch("")}>
              Clear
            </button>
          )}
          <select
            className="paramrut-year-filter"
            aria-label="Year"
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            style={{
              padding: "10px 14px",
              borderRadius: "10px",
              border: "1.5px solid rgba(102,126,234,0.4)",
              fontSize: "0.95em",
              color: "#3a3a5e",
              background: "rgba(255,255,255,0.9)",
              cursor: "pointer",
            }}
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y === "All" ? "All Years" : y}
              </option>
            ))}
          </select>
        </div>

        {/* Two-pane layout */}
        <div
          className="paramrut-layout"
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "flex-start",
            minHeight: "60vh",
          }}
        >
          {/* Left: list */}
          <div
            className="paramrut-list-panel"
            style={{
              width: selected ? "340px" : "100%",
              flexShrink: 0,
              maxHeight: "75vh",
              overflowY: "auto",
              borderRadius: "14px",
              border: "1px solid rgba(102,126,234,0.15)",
              background: "rgba(255,255,255,0.7)",
              boxShadow: "0 4px 20px rgba(102,126,234,0.08)",
            }}
          >
            {filtered.length === 0 && (
              <p
                className="paramrut-empty"
                style={{
                  padding: "24px",
                  textAlign: "center",
                  color: "#9a9abf",
                }}
              >
                No items found.
              </p>
            )}
            {filtered.map((item) => {
              const isActive = selected?.prvid === item.prvid;
              return (
                <div
                  key={item.prvid}
                  ref={isActive ? activeRef : null}
                  onClick={() => openItem(item)}
                  className={`paramrut-item${isActive ? " active" : ""}`}
                  aria-current={isActive ? "true" : undefined}
                  style={{
                    padding: "14px 16px",
                    borderBottom: "1px solid rgba(102,126,234,0.1)",
                    cursor: "pointer",
                    background: isActive
                      ? "linear-gradient(135deg,rgba(102,126,234,0.15),rgba(118,75,162,0.12))"
                      : "transparent",
                    borderLeft: isActive
                      ? "4px solid #764ba2"
                      : "4px solid transparent",
                    transition: "background 0.15s",
                  }}
                >
                  <div
                    className="paramrut-item-title"
                    style={{
                      fontWeight: 600,
                      fontSize: "0.9em",
                      color: isActive ? "#4a2472" : "#3a3a5e",
                      marginBottom: "4px",
                    }}
                  >
                    {item.prvtitle}
                  </div>
                  <div
                    className="paramrut-item-meta"
                    style={{
                      fontSize: "0.78em",
                      color: "#888",
                      display: "flex",
                      gap: "10px",
                      flexWrap: "wrap",
                    }}
                  >
                    <span>{item.prvdate}</span>
                    {item.prvplace && <span>📍 {item.prvplace}</span>}
                  </div>
                  {item.prvinfo && (
                    <div
                      className="paramrut-item-info"
                      style={{
                        fontSize: "0.75em",
                        color: "#666",
                        marginTop: "4px",
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {item.prvinfo}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Right: content pane */}
          {selected && (
            <div
              className="paramrut-content-panel"
              style={{
                flex: 1,
                minWidth: 0,
                maxHeight: "75vh",
                overflowY: "auto",
                borderRadius: "14px",
                border: "1px solid rgba(102,126,234,0.15)",
                background: "rgba(255,255,255,0.9)",
                boxShadow: "0 4px 20px rgba(102,126,234,0.1)",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Content header */}
              <div
                className="paramrut-content-header"
                style={{
                  padding: "16px 20px 12px",
                  borderBottom: "1px solid rgba(102,126,234,0.12)",
                  background:
                    "linear-gradient(135deg,rgba(102,126,234,0.08),rgba(118,75,162,0.06))",
                  borderRadius: "14px 14px 0 0",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <div
                  className="paramrut-selected-title"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px",
                  }}
                >
                  <div>
                    <div
                      className="paramrut-selected-meta"
                      style={{
                        fontWeight: 700,
                        fontSize: "1em",
                        color: "#3a3a5e",
                      }}
                    >
                      {selected.prvtitle}
                    </div>
                    <div
                      className="paramrut-selected-info"
                      style={{
                        fontSize: "0.82em",
                        color: "#888",
                        marginTop: "3px",
                      }}
                    >
                      {selected.prvdate}
                      {selected.prvplace && ` · ${selected.prvplace}`}
                    </div>
                    {selected.prvinfo && (
                      <div
                        className="paramrut-selected-description"
                        style={{
                          fontSize: "0.82em",
                          color: "#555",
                          marginTop: "5px",
                          lineHeight: 1.5,
                        }}
                      >
                        {selected.prvinfo}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelected(null)}
                    className="paramrut-close"
                    aria-label="Close reader"
                    style={{
                      flexShrink: 0,
                      background: "transparent",
                      border: "1.5px solid rgba(118,75,162,0.3)",
                      borderRadius: "8px",
                      color: "#764ba2",
                      padding: "4px 10px",
                      cursor: "pointer",
                      fontSize: "0.85em",
                    }}
                  >
                    ✕
                  </button>
                </div>

                {/* Toolbar */}
                <div
                  className="paramrut-toolbar"
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginTop: "4px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => goToRelativeItem(-1)}
                    disabled={selectedIndex <= 0}
                    title="Previous item (Left Arrow)"
                    style={toolbarBtnStyle}
                  >
                    ← Prev
                  </button>
                  <button
                    type="button"
                    onClick={() => goToRelativeItem(1)}
                    disabled={
                      selectedIndex < 0 || selectedIndex >= filtered.length - 1
                    }
                    title="Next item (Right Arrow)"
                    style={toolbarBtnStyle}
                  >
                    Next →
                  </button>
                  <button
                    type="button"
                    onClick={() => setFontSize((f) => Math.min(f + 0.1, 2))}
                    style={toolbarBtnStyle}
                  >
                    A+
                  </button>
                  <button
                    type="button"
                    onClick={() => setFontSize((f) => Math.max(f - 0.1, 0.7))}
                    style={toolbarBtnStyle}
                  >
                    A−
                  </button>
                  <button
                    type="button"
                    onClick={() => setFontSize(1)}
                    style={toolbarBtnStyle}
                  >
                    Reset
                  </button>
                  {getVideoId(selected.prvvideourl) && (
                    <button
                      type="button"
                      onClick={() => openVideo(selected)}
                      className="paramrut-video-button"
                      style={{
                        ...toolbarBtnStyle,
                        background: "linear-gradient(135deg,#ff0000,#cc0000)",
                        color: "#fff",
                        border: "none",
                      }}
                    >
                      ▶ Watch Video
                    </button>
                  )}
                </div>
              </div>

              {/* Content body */}
              <div className="paramrut-content-body" style={{ padding: "20px", flex: 1 }}>
                {contentLoading && (
                  <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading...</p>
                  </div>
                )}
                {!contentLoading && (
                  <div
                    className="paramrut-content-html"
                    style={{ fontSize: `${fontSize}em`, lineHeight: 1.8 }}
                    dangerouslySetInnerHTML={{ __html: content }}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <VideoModal
        isOpen={isModalOpen}
        onClose={closeModal}
        videoId={modalVideoId}
        title={modalVideoTitle}
      />
    </main>
  );
}

const toolbarBtnStyle = {
  padding: "4px 12px",
  background: "rgba(255,255,255,0.8)",
  border: "1.5px solid rgba(118,75,162,0.3)",
  borderRadius: "8px",
  color: "#764ba2",
  fontSize: "0.82em",
  cursor: "pointer",
  fontWeight: 600,
};
